import type { KpiComparisonPeriod } from "@types";

export type MaintenanceType = "PREVENTIVE" | "CORRECTIVE" | "SINGLE" | "SUBSCRIPTION";

export type MaintenanceStatus = "DONE" | "SCHEDULED" | "CANCELLED";

export type MaintenanceAlertSeverity = "CRITICAL" | "WARNING" | "INFO";

export type MaintenanceAlertType = "CORRECTIVE_CONCENTRATION" | "COST_PER_KM_INCREASE" | "COST_PER_VEHICLE_DECREASE";

export const MAINTENANCE_ALERT_TYPES: MaintenanceAlertType[] = [
  "CORRECTIVE_CONCENTRATION",
  "COST_PER_KM_INCREASE",
  "COST_PER_VEHICLE_DECREASE",
];

export interface MaintenanceSummaryMetric {
  value: number;
  previousValue: number;
  variationPercent: number | null;
  currentPeriod?: KpiComparisonPeriod | null;
  previousPeriod?: KpiComparisonPeriod | null;
}

export interface MaintenanceSummary {
  totalSpent: MaintenanceSummaryMetric;
  averageCostPerKm: MaintenanceSummaryMetric & { kmDriven: number };
  averageCostPerVehicle: MaintenanceSummaryMetric;
}

export interface MaintenanceTypeTotal {
  type: MaintenanceType;
  amount: number;
}

export interface MaintenanceSubcategoryTotal {
  subcategory: string;
  amount: number;
}

export interface MaintenanceVehicleCost {
  vehicle: string;
  amount: number;
  costPerKm: number;
}

export interface MaintenanceTransaction {
  id: string;
  dateTime: string;
  vehicle: string;
  type: MaintenanceType;
  subcategory: string;
  paymentMethod: string;
  amount: number;
  status: MaintenanceStatus;
}

export interface MaintenanceAlert {
  id: string;
  type: MaintenanceAlertType;
  severity: MaintenanceAlertSeverity;
  title: string;
  description: string;
  occurredAt: string;
}

export interface MaintenanceAlertTotals {
  total: number;
  alerts: Partial<Record<MaintenanceAlertType, number>>;
}

export interface MaintenanceAlertMetric {
  label: string;
  value: number;
  highlighted: boolean;
}

export interface MaintenanceAlertDetail extends MaintenanceAlert {
  metrics: MaintenanceAlertMetric[];
}

const ALERT_TEMPLATES: Record<
  MaintenanceAlertType,
  { severity: MaintenanceAlertSeverity; title: string; description: string }
> = {
  CORRECTIVE_CONCENTRATION: {
    severity: "CRITICAL",
    title: "Alta concentração em manutenção corretiva",
    description: "Manutenções corretivas representam 56% dos gastos do período.",
  },
  COST_PER_KM_INCREASE: {
    severity: "WARNING",
    title: "Aumento do custo km rodado",
    description: "O custo por km aumentou 18,9% em relação ao mês anterior.",
  },
  COST_PER_VEHICLE_DECREASE: {
    severity: "INFO",
    title: "Redução do gasto médio por veículos",
    description: "O custo médio por veículo caiu 11% neste mês.",
  },
};

const ALERT_METRICS: Record<MaintenanceAlertType, MaintenanceAlertMetric[]> = {
  CORRECTIVE_CONCENTRATION: [
    { label: "Outras revisões", value: 124800.2, highlighted: false },
    { label: "Corretiva", value: 157527.78, highlighted: true },
  ],
  COST_PER_KM_INCREASE: [
    { label: "Custo/km anterior", value: 0.42, highlighted: false },
    { label: "Custo/km atual", value: 0.5, highlighted: true },
  ],
  COST_PER_VEHICLE_DECREASE: [
    { label: "Custo médio anterior", value: 0.64, highlighted: false },
    { label: "Custo médio atual", value: 0.57, highlighted: true },
  ],
};

type AlertRow = [type: MaintenanceAlertType, occurredAt: string];

const ALERT_ROWS: AlertRow[] = [
  ["CORRECTIVE_CONCENTRATION", "2026-08-28T14:32:00"],
  ["COST_PER_KM_INCREASE", "2026-08-27T10:15:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-25T08:47:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-08-22T16:20:00"],
  ["COST_PER_KM_INCREASE", "2026-08-20T09:05:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-18T13:42:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-15T11:18:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-08-13T15:30:00"],
  ["COST_PER_KM_INCREASE", "2026-08-11T09:50:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-08T14:05:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-08-06T10:40:00"],
  ["COST_PER_KM_INCREASE", "2026-08-04T08:15:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-01T16:55:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-29T11:25:00"],
  ["COST_PER_KM_INCREASE", "2026-07-26T13:10:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-07-23T09:35:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-20T15:45:00"],
  ["COST_PER_KM_INCREASE", "2026-07-17T10:20:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-07-14T14:50:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-11T08:05:00"],
  ["COST_PER_KM_INCREASE", "2026-07-08T12:30:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-07-05T16:15:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-02T09:45:00"],
];

export const MAINTENANCE_ALERT_HISTORY_MOCK: MaintenanceAlert[] = ALERT_ROWS.map(([type, occurredAt], index) => ({
  id: String(index + 1),
  type,
  occurredAt,
  ...ALERT_TEMPLATES[type],
}));

export const buildAlertDetail = (alert: MaintenanceAlert): MaintenanceAlertDetail => ({
  ...alert,
  metrics: ALERT_METRICS[alert.type],
});
